import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  Paper,
  Snackbar,
  Alert,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
  InputAdornment
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PickersLayout } from "@mui/x-date-pickers";
import { CalendarIcon } from "@mui/x-date-pickers";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";

import AddIcon from "@mui/icons-material/Add";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScaleIcon from "@mui/icons-material/Scale";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Area,
  Scatter,
  CartesianGrid
} from "recharts";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import useThemeColor from "../../utils/useTheme";
import {
  fetchWeightData,
  postWeightData,
  deleteWeightEntry,
  fetchAvgData
} from "../../api/userApi";
import { useSelector } from "react-redux";
import useIsMobile from "../Extra/useMobile";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import DateRangePicker from "./DateRangePicker";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

const kgToLb = (kg) => kg * 2.2046226218;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
const CustomPickersDay = ({ completedDates, ...props }) => {
  const dayString = dayjs(props.day).format("YYYY-MM-DD");
  const isCompleted = completedDates?.includes(dayString);
  const styles = useThemeColor();

  return (
    <Box sx={{ position: "relative", display: "inline-block" }}>
      <PickersDay
        {...props}
        sx={{
          ...props.sx,
          borderRadius: isCompleted ? "50% !important" : "4px",
          border: isCompleted
            ? `1px solid ${styles.primaryColor} !important`
            : "none",
          "&.Mui-selected": {
            backgroundColor: `${styles.primaryColor} !important`
          }
        }}
      />
    </Box>
  );
};
const WeightChart = () => {
  const styles = useThemeColor();
  const user = useSelector((state) => state.auth.user);
  const theme = useTheme();
  const isInMobile = useIsMobile();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")) || isInMobile;
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");

  const [unit, setUnit] = useState(localStorage.getItem("weightUnit") || "lb");
  const [decimalSeparator, setDecimalSeparator] = useState(
    localStorage.getItem("decimalSeparator") || "."
  );
  const savedFilter = JSON.parse(localStorage.getItem("weightDateFilter"));

  const [dateRange, setDateRange] = useState(savedFilter?.range || "14");

  // For predefined ranges (14D, 30D), always calculate from today
  // Only use saved dates for custom range
  const getInitialDates = () => {
    const range = savedFilter?.range || "14";

    if (range === "custom") {
      return {
        start:
          savedFilter?.start ||
          dayjs.utc().subtract(13, "day").format("YYYY-MM-DD"),
        end: savedFilter?.end || dayjs.utc().format("YYYY-MM-DD")
      };
    }

    // For predefined ranges, calculate from today
    const days = parseInt(range);
    return {
      start: dayjs
        .utc()
        .subtract(days - 1, "day")
        .format("YYYY-MM-DD"),
      end: dayjs.utc().format("YYYY-MM-DD")
    };
  };

  const initialDates = getInitialDates();
  const [startDate, setStartDate] = useState(initialDates.start);
  const [endDate, setEndDate] = useState(initialDates.end);
  const [openCustomDateDialog, setOpenCustomDateDialog] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(
    savedFilter?.customStart ||
      dayjs.utc().subtract(30, "day").format("YYYY-MM-DD")
  );
  const [customEndDate, setCustomEndDate] = useState(
    savedFilter?.customEnd || dayjs.utc().format("YYYY-MM-DD")
  );
  const [globalAvg, setGlobalAvg] = useState(null);
  const [dateError, setDateError] = useState("");
  const [dateErrorMsg, setDateErrorMsg] = useState("");

  const [dailyPoints, setDailyPoints] = useState([]);
  const [allCompleteDates, setAllCompleteDates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openAdd, setOpenAdd] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [formWeight, setFormWeight] = useState("");
  const [formUnit, setFormUnit] = useState(
    localStorage.getItem("weightUnit") || "lb"
  );
  const [formDate, setFormDate] = useState(dayjs.utc().format("YYYY-MM-DD"));
  const [formDatePickerOpen, setFormDatePickerOpen] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedWeight, setSelectedWeight] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const openSnack = (msg, severity = "success") => {
    setSnackbar({ open: true, message: msg, severity });
  };

  // Helper function to format numbers with user's preferred decimal separator
  const formatNumber = (num, decimals = 1) => {
    if (num === null || num === undefined || num === "--") return num;
    const formatted = Number(num).toFixed(decimals);
    return decimalSeparator === "," ? formatted.replace(".", ",") : formatted;
  };

  // Load Data
  useEffect(() => {
    setLoading(true);
    fetchWeightData({ userId: user?._id, start: startDate, end: endDate })
      .then((res) => {
        setDailyPoints(res.data.dailyPoints || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate, user?._id]);
  useEffect(() => {
    fetchAvgData().then((res) => {
      if (res.avgKg) {
        setGlobalAvg(unit === "kg" ? res.avgKg : kgToLb(res.avgKg));
        setAllCompleteDates(res.allCompleteDates || []);
      }
    });
  }, [unit, user?._id]);
  useEffect(() => {
    localStorage.setItem(
      "weightDateFilter",
      JSON.stringify({
        range: dateRange,
        start: startDate,
        end: endDate,
        customStart: customStartDate,
        customEnd: customEndDate
      })
    );
  }, [dateRange, startDate, endDate, customStartDate, customEndDate]);
  const handleDateRangeChange = (value) => {
    if (value === "custom") {
      setOpenCustomDateDialog(true);
    } else {
      setDateRange(value);
      const days = parseInt(value);
      const newStart = dayjs
        .utc()
        .subtract(days - 1, "day")
        .format("YYYY-MM-DD");
      const newEnd = dayjs.utc().format("YYYY-MM-DD");

      setStartDate(newStart);
      setEndDate(newEnd);

      localStorage.setItem(
        "weightDateFilter",
        JSON.stringify({
          range: value,
          start: newStart,
          end: newEnd,
          customStart: customStartDate,
          customEnd: customEndDate
        })
      );
    }
  };

  const handleCustomDateApply = () => {
    if (
      dayjs(customEndDate).isBefore(customStartDate) ||
      dayjs(customEndDate).isSame(customStartDate)
    ) {
      setDateError("End date must be greater than start date");
      return;
    }

    setDateError("");
    setStartDate(customStartDate);
    setEndDate(customEndDate);
    setDateRange("custom");

    localStorage.setItem(
      "weightDateFilter",
      JSON.stringify({
        range: "custom",
        start: customStartDate,
        end: customEndDate,
        customStart: customStartDate,
        customEnd: customEndDate
      })
    );

    setOpenCustomDateDialog(false);
  };

  const chartData = useMemo(() => {
    if (!dailyPoints?.length) return [];

    const mapped = dailyPoints.map((p) => ({
      date: p.date,
      weight: unit === "kg" ? p.weightKg : kgToLb(p.weightKg),
      avg: p.avgKg ? (unit === "kg" ? p.avgKg : kgToLb(p.avgKg)) : null
    }));

    // ✅ ADD 1 EXTRA DAY PADDING ON BOTH SIDES
    const firstDate = dayjs
      .utc(mapped[0].date)
      .subtract(1, "day")
      .format("YYYY-MM-DD");
    const lastDate = dayjs
      .utc(mapped[mapped.length - 1].date)
      .add(1, "day")
      .format("YYYY-MM-DD");

    return [
      { date: firstDate, weight: null, avg: null }, // ✅ front padding
      ...mapped,
      { date: lastDate, weight: null, avg: null } // ✅ back padding
    ];
  }, [dailyPoints, unit]);

  const todayWeight = useMemo(() => {
    const todayStr = dayjs.utc().format("YYYY-MM-DD");
    const found = chartData.find((d) => d.date === todayStr);
    return found?.weight != null ? formatNumber(found.weight) : "--";
  }, [chartData, decimalSeparator]);

  const todayRollingAvg = useMemo(() => {
    const todayStr = dayjs.utc().format("YYYY-MM-DD");
    const found = chartData.find((d) => d.date === todayStr);

    return found?.avg != null ? formatNumber(found.avg) : "--";
  }, [chartData, decimalSeparator]);

  // Calculate most recent weight and average (for when today's data doesn't exist)
  const mostRecentData = useMemo(() => {
    if (!chartData.length) return null;

    // Filter out entries with null weight and padding entries
    const validEntries = chartData.filter((d) => d.weight != null);
    if (!validEntries.length) return null;

    // Get the most recent entry (last valid entry)
    const mostRecent = validEntries[validEntries.length - 1];

    return {
      date: mostRecent.date,
      weight: formatNumber(mostRecent.weight),
      avg: mostRecent.avg != null ? formatNumber(mostRecent.avg) : "--"
    };
  }, [chartData, decimalSeparator]);

  // Check if today's data has been entered
  const hasTodayData = todayWeight !== "--";

  const stats = useMemo(() => {
    if (!chartData.length) return null;

    const weights = chartData.map((d) => d.weight).filter(Boolean);
    if (!weights.length) return null;

    const start = weights[0];
    const current = weights[weights.length - 1];
    const change = current - start;
    const changePercent = ((change / start) * 100).toFixed(1);

    return {
      current,
      change: formatNumber(change),
      changePercent: decimalSeparator === "," ? changePercent.replace(".", ",") : changePercent,
      avg: formatNumber(weights.reduce((a, b) => a + b, 0) / weights.length),
      min: formatNumber(Math.min(...weights)),
      max: formatNumber(Math.max(...weights)),
      isGain: change >= 0,
      entries: weights.length
    };
  }, [chartData, decimalSeparator]);

  // Add Entry
  const handleAddEntry = async () => {
    if (!formWeight || !formDate) {
      openSnack("Please enter weight and date", "error");
      return;
    }

    try {
      const timestamp = dayjs.utc(formDate).startOf("day").toISOString();
      // Normalize comma to dot for backend processing
      const normalizedWeight = formWeight.replace(',', '.');
      const res = await postWeightData({
        userId: user?._id,
        timestamp,
        weight: Number(normalizedWeight),
        unit: formUnit
      });

      if (res.message === "updated") {
        openSnack("Weight updated for this date!");
      } else {
        openSnack("Weight entry created!");
      }

      // Toggle unit view to match the entry unit
      if (formUnit !== unit) {
        setUnit(formUnit);
        localStorage.setItem("weightUnit", formUnit);
      }

      setOpenAdd(false);
      setFormWeight("");
      setFormDate(dayjs.utc().format("YYYY-MM-DD"));
      const updatedDates = await fetchAvgData();
      const fresh = await fetchWeightData({
        userId: user?._id,
        start: startDate,
        end: endDate
      });
      setDailyPoints(fresh.data.dailyPoints || []);
      setAllCompleteDates(updatedDates.allCompleteDates || []);
    } catch (err) {
      console.log("Error adding entry", err);
      console.error(err);
      openSnack("Error adding entry", "error");
    }
  };

  // Y-Axis Range - Properly handles unit conversion
  const yDomain = useMemo(() => {
    if (chartData.length === 0) {
      // Default range based on unit
      return unit === "kg" ? [0, 100] : [0, 220];
    }

    const values = chartData
      .flatMap((d) => [d.weight, d.avg])
      .filter((v) => v !== null && v !== undefined && !isNaN(v));

    if (values.length === 0) {
      return unit === "kg" ? [0, 100] : [0, 220];
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;

    // Adjust padding based on unit and range
    const padPercentage = 0.2; // 20% padding
    const pad = Math.max(unit === "kg" ? 3 : 8, range * padPercentage);

    const roundToNice = (val, direction) => {
      // Adjust step based on unit and range
      let step;
      if (unit === "kg") {
        step = range > 50 ? 10 : range > 20 ? 5 : 2;
      } else {
        step = range > 100 ? 20 : range > 50 ? 10 : 5;
      }

      if (direction === "down") {
        return Math.floor(val / step) * step;
      }
      return Math.ceil(val / step) * step;
    };

    return [roundToNice(min - pad, "down"), roundToNice(max + pad, "up")];
  }, [chartData, unit]);

  const yAxisTicks = useMemo(() => {
    const [min, max] = yDomain;
    const range = max - min;

    let step;
    if (unit === "kg") {
      if (range <= 10) step = 1;
      else if (range <= 20) step = 2;
      else if (range <= 50) step = 5;
      else step = 10;
    } else {
      // lb
      if (range <= 20) step = 2;
      else if (range <= 50) step = 5;
      else if (range <= 100) step = 10;
      else step = 20;
    }

    const ticks = [];
    for (let i = min; i <= max; i += step) {
      ticks.push(i);
    }
    return ticks;
  }, [yDomain, unit]);

  // Custom Tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            background: styles.modBackgroundBlocks,
            border: `1px solid ${styles.lightBlue}`,
            borderRadius: 2.5,
            p: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            minWidth: 140
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              color: styles.modLabel,
              fontWeight: 500,
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: 0.5
            }}
          >
            {dayjs.utc(data.date).format("ddd, MMM D")}
          </Typography>

          {data.weight && (
            <Box display="flex" alignItems="baseline" gap={0.5} mb={0.5}>
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: 700,
                  color: styles.color,
                  lineHeight: 1
                }}
              >
                {formatNumber(data.weight)}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: styles.modLabel }}>
                {unit}
              </Typography>
            </Box>
          )}

          {data.avg && (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: `1px solid ${styles.lightBlue}`
              }}
            >
              <Typography
                sx={{
                  fontSize: "10px",
                  color: styles.modLabel,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 0.25
                }}
              >
                Rolling Avg
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: styles.primaryColor,
                  fontWeight: 600
                }}
              >
                {formatNumber(data.avg)} {unit}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }
    return null;
  };

  // Custom Scatter Point - smaller on mobile
  const CustomScatterPoint = (props) => {
    const { cx, cy, payload } = props;
    if (!cx || !cy || payload.weight === null) return null;

    const dotSize = isMobile ? 5 : 7;
    const glowSize = isMobile ? 10 : 14;

    return (
      <g>
        {/* Outer glow */}
        <circle
          cx={cx}
          cy={cy}
          r={glowSize}
          fill={styles.primaryColor}
          opacity={0.15}
        />
        {/* Main dot */}
        <circle
          cx={cx}
          cy={cy}
          r={dotSize}
          fill={styles.primaryColor}
          stroke="#fff"
          strokeWidth={isMobile ? 1.5 : 2}
          style={{
            cursor: "pointer",
            filter: `drop-shadow(0 2px 4px rgba(54, 138, 208, 0.4))`
          }}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedDate(payload.date);
            setSelectedWeight(formatNumber(payload.weight));
            setOpenDeleteDialog(true);
          }}
        />
      </g>
    );
  };

  const dateRangeOptions = [
    { value: "14", label: "14D" },
    { value: "30", label: "30D" },
    { value: "custom", label: "Custom" }
  ];

  // Common text field styles
  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      background: styles.background,
      borderRadius: 2.5,
      transition: "all 0.2s ease",

      // ✅ DEFAULT (NON-FOCUS) BORDER
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: styles.primaryColor,
        borderWidth: 1.5
      },

      // ✅ HOVER
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: styles.primaryColor
      },

      // ✅ FOCUS
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: styles.primaryColor,
        borderWidth: 2
      }
    },

    "& input": {
      color: styles.color,
      fontSize: "16px",
      fontWeight: 500,
      "&::placeholder": {
        color: styles.modLabel,
        opacity: 0.7
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: styles.background,
        pb: isMobile ? 12 : 4
      }}
    >
      {/* Header - Same as original */}
      <Box
        display="flex"
        gap="20px"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "flex-start" : "center"}
      >
        <Box
          display="flex"
          alignItems="center"
          gap="30px"
          sx={{
            ...(isMobile && {
              width: "100%",
              padding: "16px 30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              zIndex: 1000,
              background: "#1F2937",
              color: styles.color
            }),
            ...(!isMobile && {
              padding: "20px 20px 0 32px"
            })
          }}
        >
          <Box display="flex" alignItems="center" gap="10px">
            <Box>
              <Typography
                fontSize={isMobile || isTablet ? "16px" : "30px"}
                fontWeight={400}
                color={isMobile ? "#FCFCFC" : styles.color}
              >
                Weight Tracker
              </Typography>
              <Typography
                fontSize={isMobile || isTablet ? "14px" : "24px"}
                color={styles.modLabel}
                fontWeight={isMobile || isTablet ? 600 : 500}
              >
                {dayjs().format("D MMM YYYY")}
              </Typography>
            </Box>
          </Box>

          {isMobile && (
            <Button
              onClick={() => setOpenAdd(true)}
              sx={{
                textTransform: "none",
                background: "#368AD0",
                color: "#fcfcfc",
                height: "40px",
                padding: "7px 12px",
                fontSize: "14px",
                gap: "8px",
                borderRadius: "4px"
              }}
            >
              <AddIcon sx={{ height: "16px", width: "16px" }} />
              Add Entry
            </Button>
          )}
        </Box>

        {/* Desktop Add Button */}
        {!isMobile && (
          <Box sx={{ padding: "20px 20px 0 0" }}>
            <Button
              onClick={() => setOpenAdd(true)}
              sx={{
                textTransform: "none",
                background: "#368AD0",
                color: "#fcfcfc",
                height: isTablet ? "60px" : "40px",
                padding: "12px 24px",
                gap: "8px",
                borderRadius: "4px"
              }}
            >
              <AddIcon /> Add Entry
            </Button>
          </Box>
        )}
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          padding: isMobile ? "16px" : "20px 20px 40px 32px",
          mx: "auto"
        }}
      >
        {/* Current Weight Card */}
        {stats && (
          <Box
            sx={{
              background: styles.modBackgroundBlocks,
              borderRadius: 3,
              p: isMobile ? 2.5 : 3,
              mb: 2.5
            }}
          >
            <Box
              display="flex"
              flexDirection={"row"}
              alignItems={isMobile ? "flex-start" : "center"}
              justifyContent="space-between"
              gap={2}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: styles.modLabel,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    fontWeight: 600,
                    mb: 0.5
                  }}
                >
                  {hasTodayData
                    ? "Current 7-Day Average"
                    : "Most Recent Average"}
                </Typography>
                <Box display="flex" alignItems="baseline" gap={1}>
                  <Typography
                    sx={{
                      fontSize: isMobile ? "34px" : "44px",
                      fontWeight: 700,
                      color: styles.color,
                      lineHeight: 1,
                      letterSpacing: "-1px"
                    }}
                  >
                    {hasTodayData
                      ? todayRollingAvg
                      : mostRecentData?.avg || "--"}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: isMobile ? "18px" : "22px",
                      fontWeight: 500,
                      color: styles.modLabel
                    }}
                  >
                    {(
                      hasTodayData
                        ? !todayRollingAvg?.includes("--")
                        : mostRecentData?.avg !== "--"
                    )
                      ? unit
                      : ""}
                  </Typography>
                </Box>
                <Box mt={2}>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: styles.modLabel,
                      letterSpacing: 1.5,
                      textTransform: "uppercase",
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    {hasTodayData
                      ? "Today's Weight"
                      : mostRecentData?.date
                      ? `${dayjs
                          .utc(mostRecentData.date)
                          .format("D MMM YYYY")} Weight`
                      : "Today's Weight"}
                  </Typography>

                  <Box display="flex" alignItems="baseline" gap={1}>
                    <Typography
                      sx={{
                        fontSize: isMobile ? "34px" : "44px",
                        fontWeight: 700,
                        color: styles.color,
                        lineHeight: 1,
                        letterSpacing: "-1px"
                      }}
                    >
                      {hasTodayData
                        ? todayWeight
                        : mostRecentData?.weight || "--"}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: isMobile ? "18px" : "22px",
                        fontWeight: 500,
                        color: styles.modLabel
                      }}
                    >
                      {(
                        hasTodayData
                          ? !todayWeight?.includes("--")
                          : mostRecentData?.weight !== "--"
                      )
                        ? unit
                        : ""}
                    </Typography>
                  </Box>
                </Box>

                {/* <Box display="flex" alignItems="baseline" gap={1} mt={2}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: styles.modLabel,
                      letterSpacing: 1.5,
                      textTransform: "uppercase",
                      fontWeight: 600,
                      my: 1
                    }}
                  >
                    {hasTodayData
                      ? `Today's weight : ${todayWeight} ${
                          !todayWeight?.includes("--") ? unit : ""
                        }`
                      : mostRecentData
                      ? `${dayjs
                          .utc(mostRecentData.date)
                          .format("D MMM YYYY")} Weight : ${
                          mostRecentData.weight
                        } ${unit}`
                      : `Today's weight : ${todayWeight} ${
                          !todayWeight?.includes("--") ? unit : ""
                        }`}
                  </Typography>
                </Box> */}
              </Box>

              {/* Unit Toggle */}
              <ToggleButtonGroup
                value={unit}
                exclusive
                onChange={(e, v) => {
                  if (v) {
                    setUnit(v);
                    localStorage.setItem("weightUnit", v);
                  }
                }}
                size="small"
                sx={{
                  background: styles.background,
                  borderRadius: 2.5,
                  p: 0.5,
                  "& .MuiToggleButton-root": {
                    border: "none",
                    color: styles.modLabel,
                    px: 2.5,
                    py: 0.75,
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    borderRadius: "8px !important",
                    transition: "all 0.2s ease",
                    "&.Mui-selected": {
                      backgroundColor: styles.primaryColor,
                      color: "#fff",
                      boxShadow: "0 2px 8px rgba(54,138,208,0.3)"
                    },
                    "&:hover": {
                      backgroundColor: styles.primaryColor,
                      color: "#fff"
                    }
                  }
                }}
              >
                <ToggleButton value="lb">LB</ToggleButton>
                <ToggleButton value="kg">KG</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        )}

        {/* Chart Section */}
        <Box
          sx={{
            background: styles.modBackgroundBlocks,
            borderRadius: 3,
            overflow: "hidden",
            mb: 2
          }}
        >
          {/* Chart Header */}
          <Box
            sx={{
              p: isMobile ? "16px 16px 12px" : "20px 24px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5
            }}
          >
            <Typography
              sx={{
                fontSize: isMobile ? "14px" : "16px",
                fontWeight: 600,
                color: styles.color
              }}
            >
              Weight Progress
            </Typography>

            {/* Date Range Pills */}
            <Box
              sx={{
                display: "flex",
                background: styles.background,
                borderRadius: 2,
                p: 0.5,
                gap: 0.25
              }}
            >
              {dateRangeOptions.map((opt) => (
                <Box
                  key={opt.value}
                  onClick={() => handleDateRangeChange(opt.value)}
                  sx={{
                    px: isMobile ? 1.5 : 2,
                    py: 0.75,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    background:
                      dateRange === opt.value
                        ? styles.primaryColor
                        : "transparent",
                    color: dateRange === opt.value ? "#fff" : styles.modLabel,
                    fontWeight: dateRange === opt.value ? 600 : 500,
                    fontSize: isMobile ? "12px" : "13px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background:
                        dateRange === opt.value
                          ? styles.primaryColor
                          : `${styles.primaryColor}20`
                    }
                  }}
                >
                  {opt.label}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Chart Content */}
          <Box sx={{ p: isMobile ? "12px 4px 16px" : "20px 16px 24px" }}>
            {loading ? (
              <Box
                height={isMobile ? 280 : 340}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                gap={2}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    border: `3px solid ${styles.lightBlue}`,
                    borderTop: `3px solid ${styles.primaryColor}`,
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" }
                    }
                  }}
                />
                <Typography sx={{ color: styles.modLabel, fontSize: "14px" }}>
                  Loading data...
                </Typography>
              </Box>
            ) : chartData.length === 0 ? (
              <Box
                height={isMobile ? 280 : 340}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                gap={2}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: `${styles.primaryColor}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <ScaleIcon
                    sx={{ fontSize: 36, color: styles.primaryColor }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: styles.color,
                    fontSize: "16px",
                    fontWeight: 500
                  }}
                >
                  No data for selected range
                </Typography>
                <Typography
                  sx={{
                    color: styles.modLabel,
                    fontSize: "14px",
                    textAlign: "center",
                    maxWidth: 260,
                    lineHeight: 1.5
                  }}
                >
                  Start tracking your progress by adding your first weight entry
                </Typography>
                <Button
                  onClick={() => setOpenAdd(true)}
                  startIcon={<AddIcon />}
                  sx={{
                    mt: 1,
                    background: styles.primaryColor,
                    color: "#fff",
                    fontWeight: 600,
                    textTransform: "none",
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    "&:hover": {
                      background: styles.primaryColor,
                      opacity: 0.9
                    }
                  }}
                >
                  Add First Entry
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  userSelect: "none",
                  "& .recharts-wrapper": {
                    outline: "none !important",
                    cursor: "default !important",

                    // Add G tag styles here
                    "& g": {
                      userSelect: "none",
                      outline: "none !important"
                    }
                  },
                  "& .recharts-surface": {
                    outline: "none !important"
                  },
                  "& .recharts-area": {
                    pointerEvents: "none"
                  },
                  "& .recharts-cartesian-grid": {
                    pointerEvents: "none"
                  },
                  "& .recharts-xAxis, & .recharts-yAxis": {
                    pointerEvents: "none"
                  },
                  "& .recharts-area-curve": {
                    pointerEvents: "none"
                  },
                  "& .recharts-area-area": {
                    pointerEvents: "none"
                  },
                  "& .recharts-scatter-symbol": {
                    pointerEvents: "auto",
                    cursor: "pointer"
                  }
                }}
              >
                <ResponsiveContainer width="100%" height={isMobile ? 280 : 340}>
                  <ComposedChart
                    data={chartData}
                    margin={{
                      top: 20,
                      right: isMobile ? 20 : 30,
                      left: isMobile ? 5 : 10,
                      bottom: isMobile ? 45 : 15
                    }}
                  >
                    <defs>
                      <linearGradient
                        id="areaGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={styles.primaryColor}
                          stopOpacity={0.25}
                        />
                        <stop
                          offset="100%"
                          stopColor={styles.primaryColor}
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    </defs>

                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={styles.lightBlue}
                      opacity={0.3}
                      vertical={false}
                    />

                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => dayjs.utc(d).format("M/D")}
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fill: styles.modLabel,
                        fontSize: isMobile ? 9 : 10
                      }}
                      dy={10}
                      interval={0}
                      minTickGap={isMobile ? 20 : 25}
                      angle={isMobile ? -45 : 0}
                      textAnchor={isMobile ? "end" : "middle"}
                      height={isMobile ? 50 : 40}
                    />

                    <YAxis
                      domain={yDomain}
                      ticks={yAxisTicks}
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fill: styles.modLabel,
                        fontSize: isMobile ? 10 : 11
                      }}
                      dx={-5}
                      width={isMobile ? 50 : 60}
                      tickFormatter={(value) => `${Math.round(value)}`}
                    />

                    <Tooltip content={<CustomTooltip />} />

                    <Area
                      type="monotone"
                      dataKey="avg"
                      stroke={styles.primaryColor}
                      strokeWidth={isMobile ? 2 : 2.5}
                      fill="url(#areaGradient)"
                      dot={false}
                      activeDot={false}
                      isAnimationActive={true}
                      animationDuration={1000}
                      animationEasing="ease-out"
                    />

                    <Scatter
                      dataKey="weight"
                      shape={<CustomScatterPoint />}
                      isAnimationActive={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>

          {/* Chart Legend */}
          {chartData.length > 0 && (
            <Box
              sx={{
                px: isMobile ? 2 : 3,
                py: 1.5,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: isMobile ? 2.5 : 4,
                flexWrap: "wrap"
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: isMobile ? 8 : 10,
                    height: isMobile ? 8 : 10,
                    borderRadius: "50%",
                    background: styles.primaryColor,
                    border: "2px solid #fff",
                    boxShadow: `0 0 0 2px ${styles.primaryColor}30`
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "11px" : "12px",
                    color: styles.modLabel
                  }}
                >
                  Daily Weight
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: isMobile ? 20 : 24,
                    height: 3,
                    borderRadius: 1,
                    background: styles.primaryColor
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "11px" : "12px",
                    color: styles.modLabel
                  }}
                >
                  7-Day Rolling Avg
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Helper Text */}
        {chartData.length > 0 && (
          <Box sx={{ textAlign: "center", px: 2 }}>
            <Typography
              sx={{
                fontSize: "12px",
                color: styles.modLabel,
                lineHeight: 1.6
              }}
            >
              Tap on any point to delete that entry
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                color: styles.modLabel,
                opacity: 0.7,
                mt: 0.5
              }}
            >
              {stats?.entries} entries in selected range
            </Typography>
          </Box>
        )}

        {/* Stats Cards */}
        {stats && (
          <Box
            sx={{
              background: styles.modBackgroundBlocks,
              borderRadius: 3,
              p: isMobile ? 2 : 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 2
            }}
          >
            {/* === STATS GRID === */}
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={isMobile ? 1.5 : 2}
            >
              {[
                {
                  label: "Average",
                  value: stats.avg,
                  color: styles.primaryColor
                },
                { label: "Lowest", value: stats.min, color: "#F6541C" },
                { label: "Highest", value: stats.max, color: styles.yellow }
              ].map((stat) => (
                <Paper
                  key={stat.label}
                  elevation={0}
                  sx={{
                    p: isMobile ? 2 : 2.5,
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: 3,
                    textAlign: "center"
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: isMobile ? "10px" : "11px",
                      color: styles.modLabel,
                      letterSpacing: 1.1,
                      textTransform: "uppercase",
                      fontWeight: 600,
                      mb: 0.75
                    }}
                  >
                    {stat.label}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: isMobile ? "22px" : "28px",
                      color: stat.color,
                      fontWeight: 800,
                      lineHeight: 1
                    }}
                  >
                    {stat.value}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: styles.modLabel,
                      mt: 0.4
                    }}
                  >
                    {unit}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {/* === TREND INFO === */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 2,
                width: "fit-content",
                background: stats.isGain
                  ? "rgba(234,179,8,0.12)"
                  : "rgba(246,84,28,0.12)"
              }}
            >
              {stats.isGain ? (
                <TrendingUpIcon sx={{ color: styles.yellow, fontSize: 18 }} />
              ) : (
                <TrendingDownIcon sx={{ color: "#F6541C", fontSize: 18 }} />
              )}

              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: stats.isGain ? styles.yellow : "#F6541C"
                }}
              >
                {stats.change > 0 ? "+" : ""}
                {stats.change} {unit}
              </Typography>

              <Typography
                sx={{
                  fontSize: "12px",
                  color: styles.modLabel,
                  fontWeight: 500
                }}
              >
                ({stats.changePercent > 0 ? "+" : ""}
                {stats.changePercent}%)
              </Typography>
            </Box>
          </Box>
        )}

        {/* About the Weight Tracker Section */}
        <Box
          sx={{
            background: styles.modBackgroundBlocks,
            borderRadius: 3,
            p: isMobile ? 2.5 : 3.5,
            mt: 3,
            mb: 2
          }}
        >
          <Typography
            sx={{
              fontSize: isMobile ? "18px" : "24px",
              fontWeight: 700,
              color: styles.modLabel,
              mb: 2.5,
              letterSpacing: 0.5
            }}
          >
            About the Weight Tracker:
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              sx={{
                fontSize: isMobile ? "13px" : "15px",
                color: styles.modLabel,
                lineHeight: 1.8,
                fontStyle: "italic"
              }}
            >
              Due to water weight fluctuations, it's better to focus on an{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, fontStyle: "normal" }}
              >
                average weekly weight
              </Box>{" "}
              than any single day weight. This tracker is a{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, fontStyle: "normal" }}
              >
                rolling average
              </Box>{" "}
              of the most recent data up to 7 days. New averages are re-started
              if there is a break of 8 or more days of recorded data.
            </Typography>

            <Typography
              sx={{
                fontSize: isMobile ? "13px" : "15px",
                color: styles.modLabel,
                lineHeight: 1.8,
                fontStyle: "italic"
              }}
            >
              Water weight fluctuations are discussed in greater detail on{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, fontStyle: "normal" }}
              >
                Day 14
              </Box>{" "}
              of the #800gChallenge program and{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, fontStyle: "normal" }}
              >
                Day 4
              </Box>{" "}
              of the Three Pillars Method program.
            </Typography>

            <Typography
              sx={{
                fontSize: isMobile ? "13px" : "15px",
                color: styles.modLabel,
                lineHeight: 1.8,
                fontStyle: "italic"
              }}
            >
              A rolling average is likely to exhibit a{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, fontStyle: "normal" }}
              >
                slight lag
              </Box>{" "}
              from your true weight when in a consistent deficit or surplus, but
              it's accurate enough for any clinical relevance.
            </Typography>

            <Typography
              sx={{
                fontSize: isMobile ? "13px" : "15px",
                color: styles.modLabel,
                lineHeight: 1.8,
                fontStyle: "italic"
              }}
            >
              If you prefer to use{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, fontStyle: "normal" }}
              >
                measurements for progress
              </Box>{" "}
              (e.g., tape measure), use the{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, fontStyle: "normal" }}
              >
                Notes section
              </Box>{" "}
              at the bottom of each Diary day instead of this Weight Tracker.
              You can see trends in measurements recorded there when you view
              your Diet Stats.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Add Entry Dialog - Enhanced */}
      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            background: styles.modBackgroundBlocks,
            borderRadius: 4,
            m: isMobile ? 2 : 3,
            overflow: "hidden"
          }
        }}
      >
        {/* Accent Line */}
        <Box
          sx={{
            height: 4,
            background: `linear-gradient(90deg, ${styles.primaryColor} 0%, ${styles.primaryColor} 100%)`
          }}
        />

        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 2.5,
            pb: 1.5,
            px: 3
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: `${styles.primaryColor}20`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <ScaleIcon sx={{ color: styles.primaryColor, fontSize: 22 }} />
            </Box>
            <Typography
              sx={{ fontSize: "18px", fontWeight: 600, color: styles.color }}
            >
              Add Weight Entry
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOpenAdd(false)}
            sx={{
              color: styles.modLabel,
              "&:hover": {
                color: styles.color,
                background: `${styles.lightBlue}50`
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Weight Input */}
            <Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: styles.modLabel,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  mb: 1
                }}
              >
                Weight
              </Typography>

              <Box display="flex" gap={1.5} alignItems="stretch">
                {/* ✅ WEIGHT INPUT */}
                <TextField
                  fullWidth
                  placeholder={decimalSeparator === "," ? "0,00" : "0.00"}
                  type="text" // ✅ switched from number → text for full control
                  value={formWeight}
                  onChange={(e) => {
                    const val = e.target.value;

                    // Detect which separator is already in the string
                    const hasDot = val.includes('.');
                    const hasComma = val.includes(',');

                    // Prevent both separators at the same time
                    if (hasDot && hasComma) {
                      return;
                    }

                    // ✅ Allow only numbers with up to 2 decimal places using either . or ,
                    const regexDot = /^\d*\.?\d{0,2}$/;
                    const regexComma = /^\d*,?\d{0,2}$/;

                    if (val === "" || regexDot.test(val) || regexComma.test(val)) {
                      setFormWeight(val);

                      // Detect and save user's decimal separator preference
                      if (hasComma && decimalSeparator !== ",") {
                        setDecimalSeparator(",");
                        localStorage.setItem("decimalSeparator", ",");
                      } else if (hasDot && decimalSeparator !== ".") {
                        setDecimalSeparator(".");
                        localStorage.setItem("decimalSeparator", ".");
                      }
                    }
                  }}
                  inputProps={{
                    inputMode: "decimal", // ✅ mobile numeric keyboard
                    style: {
                      textAlign: "center",
                      fontSize: "24px",
                      fontWeight: 600,
                      height: "56px",
                      boxSizing: "border-box"
                    }
                  }}
                  onKeyDown={(e) => {
                    // ✅ Block invalid characters
                    if (["-", "e", "E", "+"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  sx={{
                    ...textFieldStyles,
                    height: "56px",

                    "& .MuiOutlinedInput-root": {
                      height: "56px",
                      padding: "0 12px"
                    },

                    // ✅ REMOVE SPINNERS (Chrome, Edge, Safari)
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                      {
                        WebkitAppearance: "none",
                        margin: 0
                      },

                    // ✅ REMOVE SPINNERS (Firefox)
                    "& input[type=number]": {
                      MozAppearance: "textfield"
                    }
                  }}
                />

                {/* ✅ UNIT SELECT */}
                <FormControl sx={{ minWidth: 85, height: "56px" }}>
                  <Select
                    value={formUnit}
                    onChange={(e) => setFormUnit(e.target.value)}
                    sx={{
                      height: "56px", // ✅ SAME HEIGHT
                      background: styles.background,
                      borderRadius: 2.5,
                      color: styles.color,
                      fontWeight: 600,
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center", // ✅ CENTER TEXT
                      "& fieldset": {
                        borderColor: styles.lightBlue,
                        borderWidth: 1.5
                      },
                      "&:hover fieldset": {
                        borderColor: styles.primaryColor
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: styles.primaryColor
                      },
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        height: "56px",
                        padding: "0 16px"
                      },
                      "& .MuiSelect-icon": {
                        color: styles.modLabel
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: styles.modBackgroundBlocks,
                          border: `1px solid ${styles.lightBlue}`,
                          borderRadius: 2,
                          mt: 0.5,
                          "& .MuiMenuItem-root": {
                            color: styles.color,
                            fontWeight: 500,
                            "&:hover": {
                              background: `${styles.primaryColor}20`
                            },
                            "&.Mui-selected": {
                              background: `${styles.primaryColor}30`,
                              "&:hover": {
                                background: `${styles.primaryColor}40`
                              }
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="lb">lb</MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Date Input */}
            <Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: styles.modLabel,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  mb: 1
                }}
              >
                Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={dayjs(formDate)}
                  onChange={(newDate) => {
                    setFormDate(newDate ? newDate.format("YYYY-MM-DD") : "");
                  }}
                  maxDate={dayjs()}
                  onError={(reason) => {
                    setDateErrorMsg(reason);
                  }}
                  open={formDatePickerOpen}
                  onOpen={() => setFormDatePickerOpen(true)}
                  onClose={() => setFormDatePickerOpen(false)}
                  disableFuture
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      background: styles.background,
                      borderRadius: 2.5,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: styles.primaryColor,
                        borderWidth: 1.5
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: styles.primaryColor
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: styles.primaryColor,
                        borderWidth: 2
                      }
                    },
                    "& input": {
                      color: styles.color,
                      fontSize: "16px",
                      fontWeight: 500
                    },
                    "& .MuiInputLabel-root": {
                      color: styles.modLabel
                    }
                  }}
                  slots={{
                    day: (dayProps) => (
                      <CustomPickersDay
                        {...dayProps}
                        completedDates={allCompleteDates}
                      />
                    ),

                    openPickerIcon: () => (
                      <CalendarIcon sx={{ color: styles.color }} />
                    ),
                    layout: (layoutProps) => {
                      const { children, ...other } = layoutProps;
                      return (
                        <PickersLayout
                          {...other}
                          sx={{
                            "& .MuiPickersLayout-contentWrapper": {
                              paddingTop: 0
                            },
                            "& .MuiDialogTitle-root": {
                              display: "none"
                            },
                            "& .MuiPickersToolbar-root": {
                              display: "none"
                            }
                          }}
                        >
                          {children}
                          <Box
                            sx={{
                              width: "100%",
                              borderTop: "1px solid rgba(255,255,255,0.12)",
                              marginTop: "8px"
                            }}
                          >
                            <Button
                              onClick={() => {
                                const today = dayjs();
                                setFormDate(today.format("YYYY-MM-DD"));
                                setFormDatePickerOpen(false);
                              }}
                              sx={{
                                color: styles.primaryColor,
                                width: "100%",
                                padding: "8px",
                                "&:hover": {
                                  backgroundColor: "transparent"
                                }
                              }}
                            >
                              Today
                            </Button>
                          </Box>
                        </PickersLayout>
                      );
                    }
                  }}
                  slotProps={{
                    textField: {
                      error: Boolean(dateErrorMsg),
                      helperText:
                        dateErrorMsg === "maxDate"
                          ? "Date cannot be in the future"
                          : dateErrorMsg === "invalidDate"
                          ? "Invalid date format"
                          : ""
                    },
                    popper: {
                      container: () => document.body,
                      sx: {
                        zIndex: 1500000,
                        "& .MuiPaper-root": {
                          backgroundColor: styles.background,
                          color: "white",
                          ".MuiPickersLayout-root": {
                            display: "block",
                            ".MuiPickersLayout-contentWrapper": {
                              ".MuiDateCalendar-root": {
                                height: "100%",
                                ".MuiYearCalendar-root": {
                                  height: "100%",
                                  "& .MuiPickersYear-yearButton": {
                                    color: styles.color,
                                    borderRadius: "4px",
                                    "&.Mui-selected": {
                                      backgroundColor: styles.primaryColor,
                                      "&:hover": {
                                        backgroundColor: styles.primaryColor
                                      }
                                    },
                                    "&.Mui-disabled": {
                                      color: styles.disabledColor
                                    }
                                  }
                                },
                                ".MuiPickersSlideTransition-root ": {
                                  minHeight: "200px",
                                  ".MuiPickersDay-root": {
                                    color: styles.color,
                                    borderRadius: "4px",
                                    "&.Mui-disabled": {
                                      color: styles.disabledColor
                                    }
                                  }
                                }
                              }
                            }
                          }
                        },
                        ".MuiPickersCalendarHeader-label": {
                          color: styles.primaryColor
                        },
                        "& .MuiDayCalendar-weekDayLabel": {
                          color: styles.color
                        },
                        "& .MuiPickersCalendarHeader-switchViewButton": {
                          color: "white"
                        },
                        "& .MuiPickersArrowSwitcher-button": {
                          color: styles.color,
                          "&.Mui-disabled": {
                            color: "#9e9e9e !important",
                            opacity: 1
                          }
                        },
                        "& .MuiPickersYear-root": {
                          color: styles.color
                        },
                        "& .MuiPickersMonth-root": {
                          color: styles.color,
                          borderRadius: "4px",
                          "&.Mui-selected": {
                            backgroundColor: styles.primaryColor,
                            "&:hover": {
                              backgroundColor: styles.primaryColor
                            }
                          },
                          "&.Mui-disabled": {
                            color: styles.disabledColor
                          }
                        },
                        "& .MuiPickersDay-root": {
                          color: styles.color,
                          "&.Mui-selected": {
                            backgroundColor: `${styles.primaryColor} !important`
                          }
                        },
                        "& .MuiYearCalendar-root": {
                          "&::-webkit-scrollbar": {
                            width: "4px"
                          },
                          "&::-webkit-scrollbar-track": {
                            background: styles.background
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: styles.primaryColor,
                            borderRadius: "4px"
                          }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
            {dateErrorMsg === "disableFuture" && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#F6541C",
                  mt: -1.5,
                  mb: 1,
                  textAlign: "left"
                }}
              >
                {"Date cannot be in the future"}
              </Typography>
            )}
            {/* Quick Date Buttons */}
            <Box display="flex" gap={1}>
              {[
                { label: "Today", value: dayjs.utc().format("YYYY-MM-DD") },
                {
                  label: "Yesterday",
                  value: dayjs.utc().subtract(1, "day").format("YYYY-MM-DD")
                }
              ].map((item) => (
                <Button
                  key={item.label}
                  variant="contained"
                  size="small"
                  onClick={() => setFormDate(item.value)}
                  sx={{
                    flex: 1,
                    borderColor:
                      formDate === item.value
                        ? styles.primaryColor
                        : styles.lightBlue,
                    background:
                      formDate === item.value
                        ? `${styles.primaryColor}`
                        : "transparent",
                    color: styles.color,
                    fontWeight: 500,
                    textTransform: "none",
                    borderRadius: 2,
                    py: 1,
                    "&:hover": {
                      borderColor: styles.primaryColor,
                      background: `${styles.primaryColor}`
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            fullWidth
            onClick={handleAddEntry}
            disabled={!formWeight || Boolean(dateErrorMsg)}
            sx={{
              background: formWeight
                ? styles.primaryColor
                : `${styles.primaryColor}50`,
              color: "#fff",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 3,
              py: 1.5,
              fontSize: "16px",
              boxShadow: formWeight
                ? "0 4px 14px rgba(54,138,208,0.35)"
                : "none",
              transition: "all 0.2s ease",
              "&:hover": {
                background: styles.primaryColor,
                boxShadow: "0 6px 20px rgba(54,138,208,0.4)",
                transform: "translateY(-1px)"
              },
              "&:disabled": {
                color: "#fff",
                opacity: 0.7
              }
            }}
          >
            Save Entry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog - Enhanced */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            background: styles.modBackgroundBlocks,
            borderRadius: 4,
            m: isMobile ? 2 : 3,
            overflow: "hidden"
          }
        }}
      >
        <DialogContent sx={{ textAlign: "center", pt: 4, px: 3 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "rgba(246,84,28,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.5
            }}
          >
            <DeleteOutlineIcon sx={{ color: "#F6541C", fontSize: 32 }} />
          </Box>

          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 600,
              color: styles.color,
              mb: 1
            }}
          >
            Delete Entry?
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              color: styles.modLabel,
              mb: 2.5
            }}
          >
            This action cannot be undone
          </Typography>

          <Box
            sx={{
              background: styles.background,
              borderRadius: 3,
              p: 2.5,
              border: `1px solid ${styles.lightBlue}`
            }}
          >
            <Box
              display="flex"
              alignItems="baseline"
              justifyContent="center"
              gap={0.5}
            >
              <Typography
                sx={{
                  fontSize: "32px",
                  fontWeight: 700,
                  color: styles.color
                }}
              >
                {selectedWeight}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: styles.modLabel,
                  fontWeight: 500
                }}
              >
                {unit}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                color: styles.modLabel,
                mt: 0.75
              }}
            >
              {selectedDate &&
                dayjs.utc(selectedDate).format("dddd, MMMM D, YYYY")}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2, gap: 1.5 }}>
          <Button
            fullWidth
            onClick={() => setOpenDeleteDialog(false)}
            sx={{
              background: styles.background,
              border: `1.5px solid ${styles.lightBlue}`,
              color: styles.color,
              fontWeight: 500,
              textTransform: "none",
              borderRadius: 3,
              py: 1.25,
              "&:hover": {
                background: `${styles.lightBlue}30`,
                borderColor: styles.modLabel
              }
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            onClick={async () => {
              try {
                await deleteWeightEntry(user?._id, selectedDate);
                openSnack("Entry deleted successfully");

                const fresh = await fetchWeightData({
                  userId: user?._id,
                  start: startDate,
                  end: endDate
                });
                setDailyPoints(fresh.data.dailyPoints || []);
                setOpenDeleteDialog(false);
              } catch (err) {
                console.error(err);
                openSnack("Failed to delete entry", "error");
              }
            }}
            sx={{
              background: "#F6541C",
              color: "#fff",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 3,
              py: 1.25,
              boxShadow: "0 4px 14px rgba(246,84,28,0.35)",
              "&:hover": {
                background: "#DC2626",
                boxShadow: "0 6px 20px rgba(246,84,28,0.4)"
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Date Range Dialog */}
      <Dialog
        open={openCustomDateDialog}
        onClose={() => {
          setOpenCustomDateDialog(false);
          setDateError("");
        }}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            background: styles.modBackgroundBlocks,
            borderRadius: 4,
            m: isMobile ? 2 : 3,
            overflow: "hidden"
          }
        }}
      >
        {/* Accent Line */}
        <Box
          sx={{
            height: 4,
            background: `linear-gradient(90deg, ${styles.primaryColor} 100%)`
          }}
        />

        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 2.5,
            pb: 1.5,
            px: 3
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: `${styles.primaryColor}20`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <CalendarTodayIcon
                sx={{ color: styles.primaryColor, fontSize: 22 }}
              />
            </Box>
            <Typography
              sx={{ fontSize: "18px", fontWeight: 600, color: styles.color }}
            >
              Custom Date Range
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setOpenCustomDateDialog(false);
              setDateError("");
            }}
            sx={{
              color: styles.modLabel,
              "&:hover": {
                color: styles.color,
                background: `${styles.lightBlue}50`
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            {/* Date Range Picker */}
            <DateRangePicker
              startDate={dayjs(customStartDate)}
              setStartDate={(newDate) => {
                setCustomStartDate(newDate ? newDate.format("YYYY-MM-DD") : "");
                setDateError("");
              }}
              endDate={dayjs(customEndDate)}
              setEndDate={(newDate) => {
                setCustomEndDate(newDate ? newDate.format("YYYY-MM-DD") : "");
                setDateError("");
              }}
              allCompletedData={allCompleteDates || []}
              isTodayNeeded={true}
            />

            {/* Error Message */}
            {dateError && (
              <Box
                sx={{
                  background: "rgba(246,84,28,0.12)",
                  borderRadius: 2,
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#F6541C",
                    fontWeight: 500
                  }}
                >
                  {dateError}
                </Typography>
              </Box>
            )}

            {/* Date Range Info */}
            {!dateError && customStartDate && customEndDate && (
              <Box
                sx={{
                  background: styles.background,
                  borderRadius: 2.5,
                  p: 2,
                  border: `1px solid ${styles.lightBlue}`
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: styles.modLabel,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontWeight: 600,
                    mb: 0.5
                  }}
                >
                  Selected Range
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: styles.color,
                    fontWeight: 500
                  }}
                >
                  {dayjs(customStartDate).format("MMM D, YYYY")} -{" "}
                  {dayjs(customEndDate).format("MMM D, YYYY")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: styles.modLabel,
                    mt: 0.5
                  }}
                >
                  {dayjs(customEndDate).diff(dayjs(customStartDate), "day") + 1}{" "}
                  days
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2, gap: 1.5 }}>
          <Button
            fullWidth
            onClick={() => {
              setOpenCustomDateDialog(false);
              setDateError("");
            }}
            sx={{
              background: styles.background,
              border: `1.5px solid ${styles.lightBlue}`,
              color: styles.color,
              fontWeight: 500,
              textTransform: "none",
              borderRadius: 3,
              py: 1.25,
              "&:hover": {
                background: `${styles.lightBlue}30`,
                borderColor: styles.modLabel
              }
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            onClick={handleCustomDateApply}
            sx={{
              background: styles.primaryColor,
              color: "#fff",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 3,
              py: 1.25,
              boxShadow: "0 4px 14px rgba(54,138,208,0.35)",
              "&:hover": {
                background: styles.primaryColor,
                boxShadow: "0 6px 20px rgba(54,138,208,0.4)",
                transform: "translateY(-1px)"
              }
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2.5,
            fontWeight: 500,
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)"
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WeightChart;
